import { PropertyType } from '@technology/web3d-plugin-trimbim';
import { t } from '@trimble/i18n';
import { FormattingSettings } from '@trimble/types';
import { toMedDateTimeString } from '@trimble/utilities';
import type {
  ObjectPropertiesPanelCommonProperty,
  ObjectPropertiesPanelValue,
} from '@trimble/web3d-capabilities';
import type { ObjectPropertiesSourceUnits } from './object-properties.types';
import {
  formatMeasureDisplay,
  resolveMeasureSourceUnits,
} from './object-properties-measure-formatting';

const EMPTY_VALUE_PLACEHOLDER = '—';

/**
 * Fallback precision for `DoubleValue`-typed values when `FormattingSettings`
 * provides no explicit value. Matches the workspace-wide default (see
 * `formatting-settings-defaults.ts`) and the React PropertyPanel behaviour.
 */
const DEFAULT_NUMERIC_DECIMALS = 3;

/** ECMAScript `Number.prototype.toFixed` accepts digits in `[0, 100]`. */
const MIN_TO_FIXED_DIGITS = 0;
const MAX_TO_FIXED_DIGITS = 100;

/**
 * Inputs needed to render unit-aware property values
 * (Length / Area / Volume / Angle / Double) with the same conventions as the
 * React PropertyPanel in tcweb. Date/DateTime use the formatter defaults
 * (`en-US`) until locale is plumbed end-to-end.
 */
export interface ObjectPropertyFormattingContext {
  settings: FormattingSettings;
  sourceUnits?: ObjectPropertiesSourceUnits;
}

/**
 * Coerces an arbitrary "decimals" setting into a valid argument for
 * {@link Number.prototype.toFixed}. Settings can arrive as `undefined`/`null`
 * (no preference), as floats, or — defensively — as out-of-range integers
 * for which `toFixed` would throw `RangeError`.
 */
function clampDecimals(decimals: number | null | undefined): number {
  if (
    decimals === undefined ||
    decimals === null ||
    decimals < 0 ||
    !Number.isFinite(decimals)
  ) {
    return DEFAULT_NUMERIC_DECIMALS;
  }
  const truncated = Math.trunc(decimals);
  return Math.min(
    MAX_TO_FIXED_DIGITS,
    Math.max(MIN_TO_FIXED_DIGITS, truncated)
  );
}

function coerceBoolean(value: ObjectPropertiesPanelValue): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 0) return false;
    if (value === 1) return true;
    return undefined;
  }
  if (typeof value === 'bigint') {
    if (value === BigInt(0)) return false;
    if (value === BigInt(1)) return true;
    return undefined;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return undefined;
}

function formatTypedNumber(
  value: number,
  type: number,
  formatting: ObjectPropertyFormattingContext
): string | undefined {
  const { settings } = formatting;
  const measureSourceUnits = resolveMeasureSourceUnits(formatting.sourceUnits);
  switch (type) {
    case PropertyType.LengthMeasure:
      return formatMeasureDisplay(
        'length',
        value,
        settings,
        measureSourceUnits
      );
    case PropertyType.AreaMeasure:
      return formatMeasureDisplay('area', value, settings, measureSourceUnits);
    case PropertyType.VolumeMeasure:
      return formatMeasureDisplay(
        'volume',
        value,
        settings,
        measureSourceUnits
      );
    case PropertyType.AngleMeasure:
      return formatMeasureDisplay('angle', value, settings, measureSourceUnits);
    case PropertyType.DoubleValue:
      return value.toFixed(clampDecimals(settings.lengthDecimals));
    case PropertyType.IntValue:
      return Math.trunc(value).toString();
    default:
      return undefined;
  }
}

/** Threshold above which an epoch is interpreted as milliseconds, not seconds. */
const EPOCH_SECONDS_UPPER_BOUND = 1e11;

/**
 * Builds a `Date` from the various wire shapes upstream may send for a
 * `DateTime`-typed property:
 *  - `bigint`: canonical 64-bit Unix epoch (seconds or milliseconds).
 *  - `number`: same, fits comfortably within `Number.MAX_SAFE_INTEGER`.
 *  - `{ high, low }`: legacy long-pair, recomposed into a bigint first so the
 *    high half isn't lost to JS Number precision (53 bits).
 */
export function toDate(value: ObjectPropertiesPanelValue): Date | undefined {
  let epoch: bigint | undefined;

  if (typeof value === 'bigint') {
    epoch = value;
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    epoch = BigInt(Math.trunc(value));
  } else if (
    typeof value === 'object' &&
    value !== null &&
    'high' in value &&
    'low' in value
  ) {
    const LOW_HALF_MASK = 0xffffffffn;
    const HIGH_HALF_SHIFT = 32n;
    epoch =
      (BigInt(value.high) << HIGH_HALF_SHIFT) |
      (BigInt(value.low) & LOW_HALF_MASK);
  }
  if (epoch === undefined) return undefined;

  const millis =
    epoch < BigInt(EPOCH_SECONDS_UPPER_BOUND) &&
    epoch > -BigInt(EPOCH_SECONDS_UPPER_BOUND)
      ? Number(epoch) * 1000 // eslint-disable-line no-magic-numbers
      : Number(epoch);
  const date = new Date(millis);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function formatDisplayValue(
  value: ObjectPropertiesPanelValue,
  type?: number,
  formatting?: ObjectPropertyFormattingContext
): string {
  if (value === undefined || value === null) return EMPTY_VALUE_PLACEHOLDER;

  if (type === PropertyType.Boolean) {
    const bool = coerceBoolean(value);
    if (bool !== undefined) {
      return bool ? t('objectProperties.true') : t('objectProperties.false');
    }
  }

  if (typeof value === 'boolean') {
    return value ? t('objectProperties.true') : t('objectProperties.false');
  }

  if (type === PropertyType.DateTime) {
    const date = toDate(value);
    if (date !== undefined) return toMedDateTimeString(date);
  }

  if (
    formatting !== undefined &&
    type !== undefined &&
    typeof value === 'number'
  ) {
    const display = formatTypedNumber(value, type, formatting);
    if (display !== undefined) return display;
  }

  if (typeof value === 'object' && 'high' in value && 'low' in value) {
    return `${value.high} / ${value.low}`;
  }

  if (typeof value === 'bigint') return value.toString();

  return String(value);
}

export function formatCommonPropValue(
  prop: ObjectPropertiesPanelCommonProperty,
  formatting?: ObjectPropertyFormattingContext
): string {
  if (!prop.isSameValueAcrossSelection) {
    return getVariesLabel();
  }
  return formatDisplayValue(
    prop.displayProperty.value,
    prop.displayProperty.type,
    formatting
  );
}

/** Localized "Varies" label shown when a property differs across the selection. */
export function getVariesLabel(): string {
  return t('objectProperties.varies');
}
